import SendForm from './SendForm';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    filterBalances,
    useJunoCommonFunctions,
    validateAmount,
} from '../../../../client/Juno/helpers';
import { apiService } from '../../../../client/common/apiCallService';
import SendModalChooser from './SendModalChooser';
import { filterNetworkSpecificAddresses } from './sendHelpers';
import PropTypes from 'prop-types';

const SendCrypto = ({ isModal, onClose, row, parentOnBack, parentGetCryptoAddress }) => {
    const { balancesCrypto, unfreezeCryptoCurrencies } = useSelector(state => state.juno);
    const updatedUnfreeze = filterBalances(unfreezeCryptoCurrencies);

    const [selectedCrypto, setSelectedCrypto] = useState(() => {
        let firstCrypto = row
            ? updatedUnfreeze?.find(crypto => crypto?.currencyShortName == row?.currencyShortName)
            : updatedUnfreeze[0];

        return {
            selected: firstCrypto,
            selectedBlockain: row?.blockchain || firstCrypto?.blockchain,
            selectedBlockainUsersAddress: '',
            loading: true,
            selectedToAddress: '',
            selectedToAddressFormated: '',
            selectedToAddressName: '',
            selectedBlockainFee: 0,
        };
    });

    let [modals, setModals] = useState({
        open: isModal != undefined ? true : false,
        current: 'send',
    });

    const [amount, setAmount] = useState({ value: null, error: null });

    const [allAddresses, setAllAddresses] = useState({
        addresses: null,
        loading: false,
    });

    const clientData = useSelector(state => state.juno.clientData);
    const apiUrl = useSelector(state => state.config.api_url);

    const { getCryptoBalances, getCryptoFee, getWalletAddress } = useJunoCommonFunctions();

    // fetch users address for a particular network
    const fetchUsersAddress = async chain => {
        try {
            const { currencyShortName: cryptoId } = selectedCrypto.selected;
            const normalizedCryptoId =
                cryptoId === 'USDT' && (chain || selectedCrypto.selectedBlockain) === 'Tron'
                    ? 'TRC20-USDT'
                    : cryptoId;

            const blockchain = balancesCrypto.find(
                crypto => crypto?.currencyShortName === normalizedCryptoId,
            )?.blockchain;
            if (!blockchain) return;

            const response = await getWalletAddress({
                blockchainList: [blockchain],
                accountNumber: clientData?.accountNumber,
            });

            const address = response?.data?.[0]?.address?.key ?? null;
            setSelectedCrypto(prev => ({
                ...prev,
                selectedBlockainUsersAddress: address,
            }));
        } catch (error) {
            console.error('Error fetching wallet address:', error);
        }
    };

    // grab crypto address
    const getCryptoAddress = () => {
        apiService(
            (window.location.hostname === 'localhost' ? apiUrl : '') +
                '/restapi/get-crypto-address',
            {
                clientId: clientData?.clientId,
            },
            async data => {
                if (data) {
                    if (row?.address) {
                        const moveToFirst = data.find(
                            addressItem => addressItem.address == row.address,
                        );
                        if (moveToFirst) {
                            data = data.filter(addressItem => addressItem.address != row.address);

                            data = [moveToFirst, ...data];
                        }
                    }

                    setAllAddresses(prev => ({
                        ...prev,
                        addresses: data,
                    }));
                }
            },
            err => {
                console.log(err);
            },
        );
    };

    // grab crypto fee
    const fetchCryptoFee = async () => {
        try {
            const payload = {
                crypto: selectedCrypto?.selected?.currencyShortName,
                profile: clientData.feeProfile,
                transactionType: 'Send',
                clientId: clientData.clientId,
            };

            const cryptoFee = await getCryptoFee(payload);
            const newFee = !cryptoFee?.error ? cryptoFee?.data?.fee : 0;
            setSelectedCrypto(prev => ({
                ...prev,
                selectedBlockainFee: newFee,
            }));

            // If the blockchain network changes, validate the balance for the updated crypto
            let { value } = amount;
            if (value) {
                const total =
                    parseFloat(value) > 0
                        ? parseFloat(value) + parseFloat(newFee)
                        : parseFloat(value);

                const cryptoRangeError = validateAmount(
                    total,
                    0,
                    selectedCrypto?.selected?.currencyShortName,
                    balancesCrypto,
                );

                setAmount(prev => ({
                    ...prev,
                    error: cryptoRangeError,
                }));
            }
        } catch (error) {
            console.log(error);
        }
    };

    const setupData = async chain => {
        await Promise.all(
            [fetchCryptoFee(), fetchUsersAddress(chain)].map(promise =>
                promise.catch(error => console.error(error)),
            ),
        );
    };

    useEffect(() => {
        getCryptoBalances();
        getCryptoAddress();
    }, []);

    useEffect(() => {
        setupData(selectedCrypto.selected?.currencyShortName);
    }, [selectedCrypto.selected?.currencyShortName]);

    useEffect(() => {
        if (allAddresses.addresses) {
            let correctChain;

            if (
                selectedCrypto?.selected?.currencyShortName === 'USDT' &&
                ['Ethereum', 'Tron'].includes(selectedCrypto?.selectedBlockain)
            ) {
                correctChain = selectedCrypto?.selectedBlockain;
            } else {
                correctChain = selectedCrypto?.selected?.blockchain;
            }

            const addressDetails = filterNetworkSpecificAddresses(
                allAddresses.addresses,
                correctChain,
            );

            const firstAddress = addressDetails?.[0];

            setSelectedCrypto(prev => ({
                ...prev,
                selectedToAddress: firstAddress?.address,
                selectedToAddressFormated: firstAddress?.currencyShortName,
                selectedToAddressName: firstAddress?.name,
                loading: false,
                selectedBlockain: correctChain,
                selected: { ...prev.selected, blockchain: correctChain },
            }));
        }
    }, [
        selectedCrypto.selected?.currencyShortName,
        allAddresses.addresses,
        selectedCrypto.selectedBlockain,
    ]);

    const props = {
        setAmount,
        selectedCrypto,
        allAddresses,
        setModals,
        setSelectedCrypto,
        cryptoList: updatedUnfreeze,
        modals,
        balancesCrypto,
        amount,
        getCryptoAddress,
        onClose,
        fetchUsersAddress,
        parentOnBack,
        parentGetCryptoAddress,
    };

    if (!isModal) {
        return <SendForm {...props} />;
    } else {
        return <SendModalChooser {...props} />;
    }
};

SendCrypto.propTypes = {
    isModal: PropTypes.bool,
    onClose: PropTypes.func,
    row: PropTypes.shape({
        currencyShortName: PropTypes.string,
        blockchain: PropTypes.string,
    }),
};

export default SendCrypto;
